import {
  Bell,
  Check,
  CreditCard,
  XCircle,
  RefreshCw,
  Receipt,
  ShoppingBag,
  ShoppingCart,
  MessageSquare,
  MessageCircle,
  Video,
  VideoOff,
  AlertCircle,
  Send,
  Clock,
  UserPlus,
  Pencil,
  Trash2,
  AlertOctagon,
  Tag,
  Percent,
  Repeat,
  Info,
  type LucideIcon,
} from 'lucide-react';
import { NotificationType, type NotificationType as NotificationTypeT } from '@masters-club/types';

export type AdminNotificationLevel = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface AdminNotificationVisual {
  icon: LucideIcon;
  level: AdminNotificationLevel;
  /** Tailwind text-color class для иконки в bell */
  iconColorClass: string;
  /** Sonner toast level — какой метод вызвать */
  toastLevel: 'success' | 'error' | 'warning' | 'info';
}

const LEVEL_COLOR: Record<AdminNotificationLevel, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-blue-500',
  neutral: 'text-slate-400',
};

/**
 * Map NotificationType → визуальный пресет (иконка, цвет, toast level).
 * Без fallback на «Unknown» — каждый ADMIN_* тип имеет дискретное представление.
 * Если приходит тип, который мы не знаем (например legacy SYSTEM или мёртвые
 * user-facing типы) — мапим в нейтральный info, но НЕ показываем литерал "unknown".
 */
export function formatAdminNotification(type: NotificationTypeT | string): AdminNotificationVisual {
  const preset = MAP[type as NotificationTypeT];
  if (preset) {
    return {
      ...preset,
      iconColorClass: LEVEL_COLOR[preset.level],
      toastLevel: preset.level === 'neutral' ? 'info' : preset.level,
    };
  }
  return {
    icon: Info,
    level: 'info',
    iconColorClass: LEVEL_COLOR.info,
    toastLevel: 'info',
  };
}

type MapValue = Pick<AdminNotificationVisual, 'icon' | 'level'>;

const MAP: Partial<Record<NotificationTypeT, MapValue>> = {
  // Payments
  [NotificationType.ADMIN_PAYMENT_SUCCESS]: { icon: CreditCard, level: 'success' },
  [NotificationType.ADMIN_PAYMENT_FAILED]: { icon: XCircle, level: 'error' },
  [NotificationType.ADMIN_PAYMENT_REFUND]: { icon: RefreshCw, level: 'warning' },
  [NotificationType.ADMIN_FISCAL_ERROR]: { icon: Receipt, level: 'error' },

  // Orders
  [NotificationType.ADMIN_ORDER_NEW]: { icon: ShoppingBag, level: 'success' },
  [NotificationType.ADMIN_ORDER_CANCELLED]: { icon: ShoppingCart, level: 'warning' },

  // Support tickets
  [NotificationType.ADMIN_SUPPORT_TICKET_NEW]: { icon: MessageSquare, level: 'info' },
  [NotificationType.ADMIN_SUPPORT_TICKET_REPLY]: { icon: MessageCircle, level: 'info' },

  // Livestreams
  [NotificationType.ADMIN_LIVESTREAM_STARTED]: { icon: Video, level: 'success' },
  [NotificationType.ADMIN_LIVESTREAM_STREAM_LOST]: { icon: VideoOff, level: 'error' },
  [NotificationType.ADMIN_LIVESTREAM_OBS_ERROR]: { icon: AlertCircle, level: 'error' },

  // Telegram
  [NotificationType.ADMIN_TELEGRAM_SESSION_EXPIRED]: { icon: Send, level: 'warning' },
  [NotificationType.ADMIN_TELEGRAM_FLOOD_WAIT]: { icon: Clock, level: 'warning' },

  // Users
  [NotificationType.ADMIN_USER_DUPLICATE]: { icon: UserPlus, level: 'warning' },

  // Entity CRUD
  [NotificationType.ADMIN_ENTITY_CREATED]: { icon: Check, level: 'info' },
  [NotificationType.ADMIN_ENTITY_UPDATED]: { icon: Pencil, level: 'info' },
  [NotificationType.ADMIN_ENTITY_DELETED]: { icon: Trash2, level: 'warning' },

  // Errors / promo / discount / recurrent
  [NotificationType.ADMIN_ERROR_CRITICAL]: { icon: AlertOctagon, level: 'error' },
  [NotificationType.ADMIN_PROMO_LIMIT]: { icon: Tag, level: 'warning' },
  [NotificationType.ADMIN_PROMO_EXPIRED]: { icon: Tag, level: 'neutral' },
  [NotificationType.ADMIN_DISCOUNT_BUDGET_EXCEEDED]: { icon: Percent, level: 'warning' },
  [NotificationType.ADMIN_RECURRENT_FAILED]: { icon: Repeat, level: 'error' },

  // Legacy user-facing types (показываются в admin-bell если попали туда по ошибке)
  [NotificationType.BROADCAST]: { icon: Send, level: 'info' },
  [NotificationType.SYSTEM]: { icon: Bell, level: 'info' },
  [NotificationType.MARKETING]: { icon: Bell, level: 'neutral' },
};
